import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingCart, User, Menu, X, ShieldCheck, LogOut, LayoutDashboard } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/verify", label: "Verify Batch" },
  { to: "/affiliate", label: "Affiliate" },
  { to: "/shipping", label: "Shipping" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-30 bg-ape-bg/95 backdrop-blur border-b border-ape-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[70px]">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={BRAND.logoCircle} alt="APE-TIDES" className="h-12 w-12 object-contain" />
          <span className="font-display text-2xl tracking-wide leading-none hidden sm:block">
            APE<span className="text-ape-red">-</span>TIDES
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"}
              className={({ isActive }) => `font-head uppercase text-sm tracking-widest transition-colors duration-150 ${isActive ? "text-ape-red" : "text-white hover:text-ape-red"}`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex text-white hover:text-ape-red transition-colors duration-150" title="Admin">
              <LayoutDashboard size={20} />
            </Link>
          )}
          {user ? (
            <div className="hidden sm:flex items-center gap-3">
              <Link to="/account" className="text-white hover:text-ape-red transition-colors duration-150" data-testid="nav-account" title="Account">
                <User size={20} />
              </Link>
              <button onClick={doLogout} data-testid="nav-logout" className="text-white hover:text-ape-red transition-colors duration-150" title="Logout">
                <LogOut size={20} />
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden sm:flex text-white hover:text-ape-red transition-colors duration-150" data-testid="nav-login" title="Login">
              <User size={20} />
            </Link>
          )}
          <Link to="/cart" className="relative text-white hover:text-ape-red transition-colors duration-150" data-testid="nav-cart">
            <ShoppingCart size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-ape-red text-white text-[10px] font-bold font-mono2 h-5 w-5 flex items-center justify-center rounded-full" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>
          <Link to="/shop" className="hidden md:inline-flex bg-ape-red hover:bg-ape-redhover text-white font-head uppercase text-sm tracking-widest px-5 py-2.5 transition-colors duration-150" data-testid="nav-shop-cta">
            Shop All
          </Link>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ape-border bg-ape-bg px-4 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)}
              className="font-head uppercase text-sm tracking-widest py-2.5 text-white hover:text-ape-red">
              {l.label}
            </NavLink>
          ))}
          <div className="flex items-center gap-2 pt-3 mt-2 border-t border-ape-border">
            {user ? (
              <>
                <Link to="/account" onClick={() => setOpen(false)} className="font-head uppercase text-sm tracking-widest text-white">Account</Link>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="font-head uppercase text-sm tracking-widest text-ape-red ml-4">Admin</Link>}
                <button onClick={() => { setOpen(false); doLogout(); }} className="font-head uppercase text-sm tracking-widest text-white ml-auto">Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="font-head uppercase text-sm tracking-widest text-white">Login / Sign Up</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
